import {
    ThemeColorPaletteSlots,
    ThemeFontsCssPropertyName,
    ThemeProperties,
    ThemeLayoutCssPropertyName,
    FluidThemeColorPropertyNames,
    FluidThemeNames,
} from '@fluidx/office-fluid-types';
import {
    AppStylingProvider,
    MeetingNotesCalendarViewAppStylingProvider,
} from '@fluidx/host-defaults';
import { getIsDarkTheme } from 'owa-fabric-theme';
import { getIsMsHighContrast } from 'owa-high-contrast';
import { getPaletteAsRawColors } from 'owa-theme';
import { isCalendarSource, FluidOwaSource } from 'owa-fluid-validations';

let baseAppStylingProvider;
let calendarViewAppStylingProvider;

export function getAppStylingProvider(owaSource: FluidOwaSource): AppStylingProvider {
    if (isCalendarSource(owaSource)) {
        if (calendarViewAppStylingProvider) {
            return calendarViewAppStylingProvider;
        }
        calendarViewAppStylingProvider = new MeetingNotesCalendarViewAppStylingProvider();
        setThemeOnProvider(calendarViewAppStylingProvider);
        return calendarViewAppStylingProvider;
    }

    if (baseAppStylingProvider) {
        return baseAppStylingProvider;
    }
    baseAppStylingProvider = new AppStylingProvider();
    setThemeOnProvider(baseAppStylingProvider);
    return baseAppStylingProvider;
}

export function setThemeOnProvider(appStylingProvider?: AppStylingProvider) {
    const themeProperties: ThemeProperties = getThemeProperties();
    if (appStylingProvider) {
        appStylingProvider.setThemeProperties(themeProperties);
    } else {
        baseAppStylingProvider?.setThemeProperties(themeProperties);
        calendarViewAppStylingProvider?.setThemeProperties(themeProperties);
    }
}

function getThemeProperties(): ThemeProperties {
    const propertyArray: [string, string | undefined][] = [];

    const palette = getPaletteAsRawColors();

    propertyArray.push(
        [ThemeColorPaletteSlots.themePrimary, palette.themePrimary],
        [ThemeColorPaletteSlots.themeLighterAlt, palette.themeLighterAlt],
        [ThemeColorPaletteSlots.themeLighter, palette.themeLighter],
        [ThemeColorPaletteSlots.themeLight, palette.themeLight],
        [ThemeColorPaletteSlots.themeTertiary, palette.themeTertiary],
        [ThemeColorPaletteSlots.themeSecondary, palette.themeSecondary],
        [ThemeColorPaletteSlots.themeDarkAlt, palette.themeDarkAlt],
        [ThemeColorPaletteSlots.themeDark, palette.themeDark],
        [ThemeColorPaletteSlots.themeDarker, palette.themeDarker],
        [ThemeColorPaletteSlots.neutralLighterAlt, palette.neutralLighterAlt],
        [ThemeColorPaletteSlots.neutralLighter, palette.neutralLighter],
        [ThemeColorPaletteSlots.neutralLight, palette.neutralLight],
        [ThemeColorPaletteSlots.neutralQuaternaryAlt, palette.neutralQuaternaryAlt],
        [ThemeColorPaletteSlots.neutralQuaternary, palette.neutralQuaternary],
        [ThemeColorPaletteSlots.neutralTertiaryAlt, palette.neutralTertiaryAlt],
        [ThemeColorPaletteSlots.neutralTertiary, palette.neutralTertiary],
        [ThemeColorPaletteSlots.neutralSecondary, palette.neutralSecondary],
        [ThemeColorPaletteSlots.neutralPrimaryAlt, palette.neutralPrimaryAlt],
        [ThemeColorPaletteSlots.neutralPrimary, palette.neutralPrimary],
        [ThemeColorPaletteSlots.neutralDark, palette.neutralDark],
        [ThemeColorPaletteSlots.black, palette.black],
        [ThemeColorPaletteSlots.white, palette.white],
        [ThemeFontsCssPropertyName.FontSizeHeading1, '14px'],
        [ThemeFontsCssPropertyName.FontSizeHeading2, '14px'],
        [ThemeFontsCssPropertyName.FontSizeHeading3, '14px'],
        [ThemeFontsCssPropertyName.FontSizeBody, '14px'],
        [ThemeLayoutCssPropertyName.MarginTopHeading1, '24px']
    );

    if (getIsDarkTheme()) {
        propertyArray.push(
            [ThemeColorPaletteSlots.EntityRecognitionUnderline, '#8a8886'],
            [ThemeColorPaletteSlots.EntityRecognitionHoverHighlight, '#3b3a39'],
            [ThemeColorPaletteSlots.TaskRecognitionUnderline, '#8a8886'],
            [ThemeColorPaletteSlots.TaskRecognitionHoverHighlight, '#3b3a39'],
            [FluidThemeColorPropertyNames.ThemeName, FluidThemeNames.Dark]
        );
    } else {
        let themeName: string = '';
        if (getIsMsHighContrast()) {
            themeName = FluidThemeNames.HighContrast;
        } else {
            themeName = FluidThemeNames.Default;
        }

        propertyArray.push(
            [ThemeColorPaletteSlots.EntityRecognitionUnderline, '#484644'],
            [ThemeColorPaletteSlots.EntityRecognitionHoverHighlight, '#f3f2f1'],
            [ThemeColorPaletteSlots.TaskRecognitionUnderline, '#484644'],
            [ThemeColorPaletteSlots.TaskRecognitionHoverHighlight, '#f3f2f1'],
            [FluidThemeColorPropertyNames.ThemeName, themeName]
        );
    }

    return new Map(propertyArray) as Map<string, string>;
}
